import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useGetEventGroupEvents } from 'src/hooks/api/usePlaceholderEventGroup';
import { buildTicketGroupInput } from 'src/modals/PurchaseWizard/PurchaseTicketsFlow/buildTicketGroupInput';
import { newBigIntId } from 'src/utils/idUtils';
import { CatalogClient, TicketGroupInput } from 'src/WebApiController';

/**
 * Resolves an event group's member slots into `TicketGroupInput[]` for the
 * "create purchase order from a season group" flow: fetches the group's slots,
 * hydrates them into catalog events via `getCatalogFull`, and builds one ticket
 * group per member (all members — placeholders included) using the same builder
 * as the manual event-search path. Pass `groupId = null` to disable.
 */
export function useEventGroupTicketGroups(
  groupId: string | null,
  currencyCode: string
) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const { slots, isLoading: isSlotsLoading } = useGetEventGroupEvents(groupId);

  // Fetch by both id spaces so the catalog resolves allocated events (by
  // viagogoEventId) and placeholders (by posEventId), and returns the
  // posId→viagId lookup used below.
  const fetchIds = useMemo(() => {
    const ids = new Set<string>();
    for (const slot of slots) {
      if (slot.viagogoEventId != null) {
        ids.add(String(slot.viagogoEventId));
      }
      if (slot.eventMappingId != null) {
        ids.add(slot.eventMappingId);
      }
    }
    return [...ids];
  }, [slots]);

  const catalogQuery = useQuery({
    queryKey: ['getCatalogFull', 'eventGroup', groupId, fetchIds],
    enabled: fetchIds.length > 0,
    async queryFn() {
      try {
        return await new CatalogClient(
          activeAccountWebClientConfig
        ).getCatalogFull(fetchIds);
      } catch (err: unknown) {
        trackError('CatalogClient.getCatalogFull', err as ErrorTypes, {
          groupId,
        });
        throw err;
      }
    },
    refetchOnWindowFocus: false,
  });

  const ticketGroups = useMemo<TicketGroupInput[]>(() => {
    const catalog = catalogQuery.data;
    if (!catalog || slots.length === 0) {
      return [];
    }
    // Same id-allocation scheme as the manual path (newBigIntId() minus index).
    const startId = newBigIntId();
    const built: TicketGroupInput[] = [];
    slots.forEach((slot, i) => {
      if (!slot.event) {
        return;
      }
      built.push(
        buildTicketGroupInput({
          event: slot.event?.event,
          performer: slot.performer,
          venue: slot.venue,
          currencyCode,
          ticketGroupId: startId - i,
        })
      );
    });
    return built;
  }, [catalogQuery.data, slots, currencyCode]);

  return {
    slots,
    ticketGroups,
    memberCount: slots.length,
    isLoading:
      !!groupId &&
      (isSlotsLoading || (fetchIds.length > 0 && catalogQuery.isLoading)),
  };
}
